import { z } from 'zod'
import { AMBrand, LogLevel, type NearVenue, type Point, type VenueDataResponse } from 'src/common-services'
import { atom, selector, selectorFamily } from 'recoil'
import { DEFAULT_LRU_SIZE, keyFactory, mockableSelector, uncachedSelector } from 'src/utils/recoil'
import { findPlaceDetails, GooglePlace } from 'src/utils/google-places'
import sortBy from 'lodash/sortBy'
import { alphamapClient, devClient, loggingClient, venueClient } from 'src/clients'
import { hasCustomArea } from 'src/atoms/custom-area'
import { growl, growlError } from 'src/components/growl'
import { isInStorybook } from 'src/utils/storybook'
import { AMMobilityLocation } from 'src/atoms/mobility'
import Fuse from 'fuse.js'
import FREE_LOCATIONS from 'src/constants/free-locations.json'

import * as brandsAtoms from 'src/atoms/brands'
import * as brandsRankLayerAtoms from 'src/atoms/brands-rank-layer'

const key = keyFactory('search-bar')

export enum GooglePlaceOriginType {
  NONE = 'none',
  SEARCH = 'search',
  MAP = 'MAP',
}

export const isLocationSelector = atom<boolean>({
  key: 'is-location-selector',
  default: false,
})

export const searchText = atom<string>({
  key: key('search-text'),
  default: '',
})

export const googlePlaceOrigin = atom<GooglePlaceOriginType>({
  key: key('google-place-origin'),
  default: GooglePlaceOriginType.NONE,
})

export const internalGooglePlaceId = atom<string | undefined>({
  key: key('internal-google-place-id'),
  default: undefined,
})

export const googlePlaceId = selector<string | undefined>({
  key: key('google-place-id'),
  get: ({ get }) => {
    return get(internalGooglePlaceId)
  },
})

export const venueIdPlaceMatchHint = atom<string | undefined>({
  key: key('venue-id-place-match-hint'),
  default: undefined,
})

export const googleSearchString = atom<string | undefined>({
  key: key('google-search-string'),
  default: undefined,
})

export const googleSearchLoading = atom<boolean>({
  key: key('google-search-loading'),
  default: false,
})

export const googleSearchResult = atom<GooglePlace[] | undefined>({
  key: key('google-search-result'),
  default: undefined,
})

export const googleSearchPlaceIdHovered = atom<string | undefined>({
  key: key('google-search-result-hovered'),
  default: undefined,
})

export const mockGooglePlace = atom<GooglePlace | null | undefined>({
  key: key('mock-google-place'),
  default: undefined,
})

/**
 * returns the google place based on the selected user selected place id in the typeahead
 */
export const googlePlace = mockableSelector<GooglePlace | null | undefined>({
  key: key('google-place'),
  mock: mockGooglePlace,
  get: async ({ get }) => {
    if (get(hasCustomArea)) {
      return undefined
    }
    const placeId = get(internalGooglePlaceId)
    if (!placeId) {
      return undefined
    }
    const place = await findPlaceDetails(placeId)
    return place
  },
})

export const googleSearchHasNoResults = selector<boolean>({
  key: key('google-search-has-no-results'),
  get: ({ get }) => {
    const place = get(googlePlace)
    const text = get(googleSearchString)
    const results = get(googleSearchResult)
    if (text && !place && (!results || results.length === 0)) {
      return true
    }
    return false
  },
})

// TODO(ss): Use the type from the new entity type in jotai i.e. location/shopping-center/brand/address/etc.
// We just have to use these for filtering the insights for now since its in recoil
export const searchResultTypeSchema = z.enum([
  'address',
  'msa',
  'venue',
  'area',
  'alphamap-location',
  'parcel',
  'owner',
])
export type SearchResultType = z.infer<typeof searchResultTypeSchema>

const splitIntoAlphaNumericChunks = (str: string | null): string[] => {
  return (str ?? '')
    .replace(/[^a-z0-9+ ]+/gi, '')
    .split(' ')
    .filter(Boolean)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean)
}

const fillerWords = ['the', 'and', 'it', 'of', 'from', 'to', 'a', 'an']
type PartialMatch = {
  venue: NearVenue
  wordsInCommon: number
  totalWords: number
}

// exported for testing only
export function findBestVenueMatch(
  place: GooglePlace,
  venues: NearVenue[],
  venueIdHint?: string
): NearVenue | undefined {
  const placeWords = splitIntoAlphaNumericChunks(place.name)
  const importantPlaceWords = placeWords.filter((x) => !fillerWords.includes(x))

  // some venues geocode to the same place_id (like a grocery store with a water supply machine inside it)
  const exactPlaceMatches = venues.filter((x) => x.placeId === place.place_id)
  if (exactPlaceMatches.length === 1) {
    return exactPlaceMatches[0]
  }

  // sometimes there are multiple venues with the same google place id...
  // if the user clicks on a venue pin on the map we can store & use that selected pin for more accurate matching
  if (exactPlaceMatches.length > 1) {
    if (venueIdHint) {
      const exact = venues.find((x) => x.venueId === venueIdHint)
      if (exact) {
        return exact
      }
    }
    const placeZip = place.address_components.find((x) => x.types.includes('postal_code'))?.short_name
    const venuesWithZip = exactPlaceMatches.filter((x) => x.zipCode === placeZip)
    if (venuesWithZip.length === 1) {
      return venuesWithZip[0]
    }
  }

  const exactNameMatch = venues.find((x) => x.placeName === place.name)
  if (exactNameMatch) {
    return exactNameMatch
  }

  const sorted = exactPlaceMatches.length ? exactPlaceMatches : sortBy(venues, 'distance')
  // try exact matches first, and pick the closest
  const partialMatches: PartialMatch[] = []
  for (const venue of sorted) {
    const venueWords = splitIntoAlphaNumericChunks(venue.brand)
    const minWords = placeWords.length < venueWords.length ? placeWords : venueWords
    const maxWords = minWords === placeWords ? venueWords : placeWords
    const matches = minWords.filter((x) => maxWords.includes(x))
    if (matches.length === minWords.length) {
      return venue
    }
    const importantVenueWords = venueWords.filter((x) => !fillerWords.includes(x))
    const wordsInCommon = importantPlaceWords.filter((x) => importantVenueWords.includes(x)).length
    if (wordsInCommon > 0) {
      partialMatches.push({
        venue,
        wordsInCommon,
        totalWords: importantPlaceWords.length,
      })
    }
  }

  return partialMatches.shift()?.venue ?? undefined
}

export async function findVenueForGooglePlace(
  place: GooglePlace,
  venueIdHint?: string
): Promise<NearVenue | undefined> {
  const point: Point = {
    type: 'Point',
    coordinates: place.geometry.location,
  }
  const nearby = await venueClient.getNearestVenues({ point, radiusMiles: 0.1, maxNumber: 500 })
  const match = findBestVenueMatch(
    place,
    nearby.filter((x) => x.brand !== 'Primo Water'),
    venueIdHint
  )
  if (!match) {
    // HACK(bmc): this is a hack for getting newly imported unacast venues to the trade show
    try {
      const newStuff = await devClient.post('/venues/new/nearest', { point, radiusMiles: 0.1, maxNumber: 500 })
      const newMatch = findBestVenueMatch(place, newStuff as any, venueIdHint)
      return newMatch
    } catch (e) {
      return undefined
    }
  }
  return match
}

export const mockVenueMatch = atom<NearVenue | undefined>({
  key: key('mock-venue-match'),
  default: undefined,
})
/**
 * Returns the best matching venue depending on which google place is the search result
 * or undefined if no match is available.
 */
export const venueMatch = uncachedSelector<NearVenue | undefined>({
  key: key('search-venue-match'),
  get: async ({ get }) => {
    if (isInStorybook()) {
      const mock = get(mockVenueMatch)
      if (mock) {
        return mock
      }
    }
    let place = get(googlePlace)
    const matchHint = get(venueIdPlaceMatchHint)
    if (!place) {
      return undefined
    }
    // HACK(bmc): manual place match override for bad unacast data
    // this should "go away" once mobility v3 is completed but customer needs this today
    // boards/4635854041/pulses/4790218041
    if (place.place_id === 'ChIJ1-jC563584gRyzo6HPkxkOg') {
      place = await findPlaceDetails('ChIJD1ztS4H584gRBtoc1HBxh5k')
      if (!place) {
        return
      }
    }
    return findVenueForGooglePlace(place, matchHint)
  },
  lru: DEFAULT_LRU_SIZE,
})

const addressTypes = ['street_address', 'premise', 'shopping_mall', 'intersection', 'route']

export const placeIsAddress = (place: GooglePlace): boolean => {
  for (const type of addressTypes) {
    if (place.types[0] === type) {
      return true
    }
  }
  return false
}

// when we search for multiple places we need to check our system
// to see if we have foot traffic data available to display a little pink dot
// on the search bar result items
export const venuesForSearchResults = uncachedSelector<VenueDataResponse[]>({
  key: key('venues-for-search-results'),
  get: async ({ get }) => {
    const searchResults = get(googleSearchResult)
    if (!searchResults) {
      return []
    }
    const placeIds = searchResults.map((x) => x.place_id).filter(Boolean)
    try {
      return await venueClient.getVenuesByPlaceIds({ placeIds })
    } catch (e) {
      growlError(new Error('Could not load foot traffic stats'), 'Could not load foot traffic stats for search results')
      return []
    }
  },
  lru: DEFAULT_LRU_SIZE,
})

export const selectedLocationId = atom<string | undefined>({
  key: key('selected-location-id'),
  default: undefined,
})

export const selectedLocation = selector<AMMobilityLocation | undefined>({
  key: key('selected-location'),
  get: async ({ get }) => {
    // if the user specified a location, show it...don't match to google
    const selectedId = get(selectedLocationId)
    if (selectedId) {
      try {
        const location = await alphamapClient.locations.getById({ id: selectedId })
        return location
      } catch (error: any) {
        const txt = `Location with id ${selectedId} was not found`
        growl({ severity: 'error', title: 'Location error', text: txt })
        void loggingClient.logData({
          level: LogLevel.ERROR,
          message: error.message,
          data: {
            name: txt,
            stack: error.stack,
          },
        })
        return undefined
      }
    }
    return undefined
  },
})

const searchableBrands = selector<Fuse<AMBrand>>({
  key: key('searchable-brands'),
  get: async ({ get }) => {
    const amBrands = get(brandsAtoms.amBrands)
    return new Fuse(amBrands, { keys: ['name'], threshold: 0.2 })
  },
})

const brands = selectorFamily<AMBrand[], string>({
  key: key('brands'),
  get:
    (filter) =>
    async ({ get }) => {
      if (!filter) {
        return []
      }

      const allBrands = get(searchableBrands)
      return allBrands
        .search(filter)
        .slice(0, 3)
        .map((r) => r.item)
    },
})

export const searchedBrands = selector<AMBrand[]>({
  key: key('searched-brands'),
  get: async ({ get }) => {
    const filter = get(googleSearchString)
    if (!filter) {
      return []
    }
    const _selectedBrands = get(brandsRankLayerAtoms.selectedBrands)
    const _brands = get(brands(filter))

    return _brands.filter((b) => !_selectedBrands.find((sb) => sb.id === b.id))
  },
})

export const isLocationFree = selector<boolean>({
  key: key('is-location-free'),
  get: ({ get }) => {
    const location = get(selectedLocation)
    return location && FREE_LOCATIONS.find((free) => free.locationId === location.id) ? true : false
  },
})
